import React from 'react';
// FT-356c: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header raw ArrowLeft back button removed.
// EquipmentDetailView registers its onBack with useSetBackHandler.
import { AlertTriangle, Wrench, Trash2 } from 'lucide-react';

function EquipmentDetailHeader({
  name,
  chips = [],
  shortId,
  onCopyId,
  onLogDefect,
  onViewMaint,
  onDelete,
  activeTab = 0,
  onTabChange = () => {},
  tabs = ['Details', 'Guidance', 'History', 'Documents']
}) {
  // Show first 3 chips, rest collapsed
  const visible = chips.slice(0, 3);
  const overflow = chips.slice(3);

  return (
    <div className="border-b border-slate-200/70 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60 sticky top-0 z-20">
      <div className="mx-auto max-w-6xl px-3 md:px-6 py-2 md:py-3">
        {/* Row 1: title + actions
            FT-356c: raw ArrowLeft back button removed — bottom bar owns Back. */}
        <div className="flex items-center gap-2">
          <h1 className="text-base md:text-lg font-semibold leading-tight line-clamp-1 text-slate-900 min-w-0 flex-1">
            {name}
          </h1>

          <div className="ml-auto flex items-center gap-1 md:gap-2 shrink-0">
            {/* Mobile icon actions */}
            <button
              onClick={onLogDefect}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900"
              title="Log defect"
            >
              <AlertTriangle className="h-5 w-5" />
            </button>
            <button
              onClick={onViewMaint}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-slate-900"
              title="View maintenance"
            >
              <Wrench className="h-5 w-5" />
            </button>
            {onDelete && (
              <button
                onClick={onDelete}
                className="md:hidden p-2 rounded-lg hover:bg-red-50 text-red-600 hover:text-red-700"
                title="Delete equipment"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            )}

            {/* Desktop buttons */}
            <button
              onClick={onLogDefect}
              className="hidden md:inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <AlertTriangle className="h-4 w-4" />
              Log defect
            </button>
            {onDelete && (
              <button
                onClick={onDelete}
                className="hidden md:inline-flex items-center gap-2 rounded-lg border border-red-300 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            )}
            <button
              onClick={onViewMaint}
              className="hidden md:inline-flex items-center gap-2 rounded-lg bg-cyan-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-cyan-500"
            >
              <Wrench className="h-4 w-4" />
              View maintenance
            </button>
          </div>
        </div>

        {/* Row 2: chips + shortId */}
        <div className="mt-1 flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {visible.map((chip, i) => (
            <span 
              key={i} 
              className="shrink-0 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] md:text-xs text-slate-700"
            >
              {chip}
            </span>
          ))}
          {overflow.length > 0 && (
            <details className="group shrink-0">
              <summary className="list-none cursor-pointer rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] md:text-xs text-slate-700 hover:bg-slate-100">
                +{overflow.length}
              </summary>
              <div className="absolute mt-1 flex gap-1.5 bg-white border border-slate-200 rounded-lg p-2 shadow-lg z-10">
                {overflow.map((chip, i) => (
                  <span 
                    key={i} 
                    className="rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] md:text-xs text-slate-700"
                  >
                    {chip}
                  </span>
                ))}
              </div>
            </details>
          )}

          {/* Short ID pill with copy */}
          {shortId && (
            <button
              onClick={onCopyId}
              className="ml-1 shrink-0 rounded-md bg-gray-900 px-2 py-0.5 text-[11px] md:text-xs font-mono text-white hover:bg-black transition-colors"
              title="Copy full equipment ID"
            >
              {shortId}
            </button>
          )}
        </div>
      </div>

      {/* Slim sticky tabs */}
      <div className="border-t border-slate-200/70">
        <div className="mx-auto max-w-6xl px-1 md:px-6">
          <nav className="flex gap-1 overflow-x-auto scrollbar-hide">
            {tabs.map((tab, index) => (
              <button 
                key={tab}
                onClick={() => onTabChange(index)}
                className={`px-3 py-2 text-sm md:text-[15px] font-medium transition-colors border-b-2 ${
                  activeTab === index
                    ? 'border-cyan-600 text-cyan-700'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

export default EquipmentDetailHeader;